import dayjs from "dayjs";
import { imageExtensions } from "../config/image.constraints";
import { getLocalUserInfo } from "./authHelper";
import { allowedNameChars } from "./constants";
import rules from "../config/validationRules";
import utc from "dayjs/plugin/utc";
const timezone = require("dayjs/plugin/timezone");

function parseJsonValue(value) {
    try {
        if (typeof value === "string") {
            return JSON.parse(value);
        } else {
            return value;
        }
    } catch (error) {
        return value;
    }
}

const formatDropdown = (response) => {
    return response.map((item) => {
        return {
            value: item.id,
            label: typeof item.title === "string" ? item.title : item.title?.en,
        };
    });
};

/*
 * Capitalized words separated by spaces
 * e.g.: bus => Bus | web_app => Web App
 */
function capitalizeItem(item, splitSeparator) {
    return item
        .split(splitSeparator)
        .map((word) => {
            return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(" ");
}

/*
 * Capitalized words from array separated by spaces
 * e.g.: ["bus", "web_app"] => Bus, Mobile App
 */
function capitalizeArrayItems(array, splitSeparator, joinSeparator) {
    return array
        .map((item) => {
            return capitalizeItem(item, splitSeparator);
        })
        .join(joinSeparator);
}

const objectToOptions = (options) => {
    const optObjects = [];
    for (const key in options) {
        if (options.hasOwnProperty(key)) {
            const value = options[key];
            optObjects.push({ value: key, label: value });
        }
    }
    return optObjects;
};

const selectOptions = (options) => {
    return options && options.length
        ? options.map((item) => (
              <option key={item.value} value={item.value}>
                  {item.label}
              </option>
          ))
        : "";
};

function findObjectByKey(array, key, value) {
    for (let i = 0; i < array.length; i++) {
        if (array[i][key] === value) {
            return array[i];
        }
    }
    return null; // Return null if no object with the specified key and value is found
}

const userCompanyId = () => {
    const currentUser = getLocalUserInfo();
    if (currentUser && currentUser.company_id) {
        return currentUser.company_id;
    }
    return null;
};

const userCounterId = () => {
    const currentUser = getLocalUserInfo();
    if (currentUser && currentUser.counter_id) {
        return currentUser.counter_id;
    }
    return null;
};

function generateUniqueKey(length) {
    const characters =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let key = "";

    for (let i = 0; i < length; i++) {
        const randomIndex = Math.floor(Math.random() * characters.length);
        key += characters.charAt(randomIndex);
    }

    return key;
}

function makeReadableMessage(message) {
    // Split the message by spaces
    const words = message.split(" ");

    // Create an array to store human-readable words
    const humanReadableWords = [];

    // Loop through each word
    for (let word of words) {
        // Convert first character to uppercase
        const readableWord = word.charAt(0).toUpperCase() + word.slice(1);

        // Replace 'CamelCase' with 'Camel Case'
        const spacedWord = readableWord.replace(/([a-z])([A-Z])/g, "$1 $2");

        // Add the spaced word to the array
        humanReadableWords.push(spacedWord);
    }

    // Join the words into a human-readable sentence
    const humanReadableMessage = humanReadableWords.join(" ");

    return humanReadableMessage;
}

const formatDateToDMY = (dateString) => {
    const dateValue = new Date(dateString);
    return dayjs(dateValue).format("DD-MM-YYYY");
};

const formatDateToYMD = (dateString) => {
    const dateValue = new Date(dateString);
    return dayjs(dateValue).format("YYYY-MM-DD");
};

const formatDateToDMYHMA = (dateString) => {
    if (!dateString) return "";
    const dateValue = new Date(dateString);
    dayjs.extend(utc);
    return dayjs.utc(dateValue).format("DD-MM-YYYY hh:mm A");
};

const convertAndFormatDateToDMYHMA = (dateString) => {
    if (!dateString) return "";
    const dateValue = new Date(dateString);
    dayjs.extend(utc);
    dayjs.extend(timezone);
    return dayjs.tz(dateValue).format("DD-MM-YYYY hh:mm A");
};

const formatDateToDMYHMALocal = (dateString) => {
    if (!dateString) return "";
    const dateValue = new Date(dateString);
    return dayjs(dateValue).format("DD-MM-YYYY hh:mm A");
};

const formatDateToYMDHMS = (dateString, sec = true) => {
    const dateValue = new Date(dateString);
    if (sec) {
        return dayjs(dateValue).format("YYYY-MM-DD HH:mm:ss");
    }
    return dayjs(dateValue).format("YYYY-MM-DD HH:mm");
};

const formatDateToHMS = (dateString, sec = true) => {
    const dateValue = new Date(dateString);
    if (sec) {
        return dayjs(dateValue).format("HH:mm:ss");
    }
    return dayjs(dateValue).format("HH:mm");
};

const apiErrorHandler = (fields, apiError) => {
    const commonErrors = [];
    const stateItems = { ...fields };
    if (
        apiError &&
        apiError.data.error &&
        apiError.data.error &&
        apiError.data.error.errors
    ) {
        apiError.data.error.errors.forEach((err) => {
            if (stateItems.hasOwnProperty(err.property)) {
                stateItems[err.property].hasError = true;
                stateItems[err.property].message = makeReadableMessage(
                    err.message
                );
            } else {
                commonErrors.push(makeReadableMessage(err.message));
            }
        });
    } else {
        commonErrors.push("An error occurred. Please try again later.");
    }
    return { commonErrors, stateItems };
};

function sortByLabel(data) {
    return data.sort((a, b) => a.label.localeCompare(b.label));
}

function sortByTitleObjectEN(data) {
    return data.sort((a, b) => {
        const titleA = a.title.en.toLowerCase();
        const titleB = b.title.en.toLowerCase();
        if (titleA < titleB) return -1;
        if (titleA > titleB) return 1;
        return 0;
    });
}
function sortByTitleEN(data) {
    // Check if data is an array
    if (!Array.isArray(data)) {
        console.log("sortByTitleEN: Data is not an array.");
        return [];
    }

    // Check if objects in the array have the title_en property
    if (data.some((item) => !item.hasOwnProperty("title_en"))) {
        console.log(
            "sortByTitleEN: Some objects in the array don't have the title_en property."
        );
        return data;
    }

    return data.sort((a, b) => {
        const titleA = a.title_en.toLowerCase();
        const titleB = b.title_en.toLowerCase();
        if (titleA < titleB) return -1;
        if (titleA > titleB) return 1;
        return 0;
    });
}
function findSeatIndices(rowNumber, columnNumber, seats) {
    let existingIndex = -1;
    let missingIndex = -1;

    for (let i = 0; i < seats.length; i++) {
        if (
            seats[i].row_number === rowNumber &&
            seats[i].column_number === columnNumber
        ) {
            existingIndex = i;
        }
        if (
            seats[i].row_number === rowNumber &&
            seats[i].column_number > columnNumber
        ) {
            missingIndex = i;
            break; // Found where the missing seat should be inserted, so stop searching
        }
    }

    // If missingIndex is still -1, it means the missing seat should be inserted at the end
    if (missingIndex === -1) {
        missingIndex = seats.length;
    }

    return {
        existing: existingIndex,
        missing: missingIndex,
    };
}

function isTitleENUnique(data, title, id = false) {
    let titles = [];
    if (id) {
        titles = data
            .filter((item) => item.id !== id)
            .map((item) => item.title_en.toLowerCase());
    } else {
        titles = data.map((item) => item.title_en.toLowerCase());
    }
    const lowercaseTitle = title.toLowerCase();
    return !titles.includes(lowercaseTitle);
}

function firstLetterUppercase(str) {
    if (!str || str.length === 0) {
        return "";
    }
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

function checkImageURL(imageUrl, defaultImageUrl) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            resolve(imageUrl); // Image exists, return the original URL
        };
        img.onerror = () => {
            resolve(defaultImageUrl); // Image not found, return default URL
        };
        img.src = imageUrl;
    });
}

function getFileUrl(url) {
    if (url?.startsWith("http")) {
        return url;
    } else {
        return `${
            process.env.MIX_ASSET_BASE_URL || "https://stg-cms.bdtickets.com"
        }${url}`;
    }
}

function getCompanyLogo(company_configurations) {
    let imagePath = `/images/logo.png`;
    if (
        company_configurations &&
        Object.keys(company_configurations).includes("logo")
    ) {
        imagePath = company_configurations.logo;
    }
    return getFileUrl(imagePath);
}

function replacePTags(inputString) {
    if (typeof inputString === "string") {
        // Replace the first occurrence of <p> with <span>
        inputString = inputString.replace(/^<p>/, "<span>");
        // Replace the last occurrence of </p> with </span>
        inputString = inputString.replace(/<\/p>$/, "</span>");
    }
    return inputString;
}

const alphanumericPattern = /^[A-Za-z0-9]*$/;

const isPositiveNumber = (value) => value && Number(value) > 0;

function isBangla(name) {
    // Regular expression to check for alphanumeric characters and spaces only
    const regex = new RegExp(`^[০-৯0-9\u0980-\u09FF ?${allowedNameChars}]+$`);

    // Test the input name against the regex
    return regex.test(name);
}

function isValidAddress(name) {
    // Regular expression to check for address
    const regex = /^[a-zA-Z0-9 \n\r\t!"#$%&'()*+,-./:;<=>?@[\\\]^_`{|}~]*$/;

    // Test the input name against the regex
    return regex.test(name);
}

function isValidURL(value) {
    // Regular expression to check for URL
    const regex =
        /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:[0-9]{1,5})?(\/[^\s]*)?$/;

    // Test the input url against the regex
    return regex.test(value);
}

function isValidName(name, allowed = "") {
    // Regular expression to check for alphanumeric characters and spaces only
    const regex = new RegExp(`^(?=.*[a-zA-Z0-9])[a-zA-Z0-9 ${allowed}]+$`);

    // Test the input name against the regex
    return regex.test(name);
}

function isValidEmail(email) {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,20}$/;
    return regex.test(email);
}

function isValidBDPhoneNumber(phoneNumber, checkLandline = false) {
    let regex = undefined;
    if (checkLandline) {
        regex = /^((01|8801|\+8801)\d{9}|(096|88096|\+88096)\d{8})$/;
    } else {
        regex = /^((01|8801|\+8801)\d{9})$/;
    }

    return regex.test(phoneNumber);
}

function isValidBDORINPhoneNumber(phoneNumber, checkLandline = false) {
    let regex = undefined;
    if (checkLandline) {
        regex =
            /^((01|8801|\+8801)\d{9}|(096|88096|\+88096)\d{8}|(\+91|91|0)?[789]\d{9})$/;
    } else {
        regex = /^((01|8801|\+8801)\d{9}|(\+91|91|0)?[789]\d{9})$/;
    }

    return regex.test(phoneNumber);
}

function isValidDomain(domain) {
    const regex = /^[a-zA-Z0-9-]+\.[a-zA-Z]{2,20}$/;
    return regex.test(domain);
}
function isValidSubDomain(domain) {
    const regex = /^[a-zA-Z0-9]+(-[a-zA-Z0-9]+)*$/;
    return regex.test(domain);
}
// function restrictNegativeInput(num){

// }

function sortArrayByTitle(data) {
    // Check if data is an array
    if (!Array.isArray(data)) {
        console.log("sortByTitle: Data is not an array.");
        return [];
    }

    // Check if objects in the array have the title property
    if (data.some((item) => !item.hasOwnProperty("title"))) {
        console.log(
            "sortByTitle: Some objects in the array don't have the title property."
        );
        return data;
    }

    // Create a new array to avoid mutating the original one
    const dataCopy = [...data];

    return dataCopy.sort((a, b) => {
        const titleA = a.title.toLowerCase();
        const titleB = b.title.toLowerCase();
        if (titleA < titleB) return -1;
        if (titleA > titleB) return 1;
        return 0;
    });
}

function getImageInfoMessage(width, height, maxSizeInBytes) {
    return (
        <p
            id="helper-text-explanation"
            className="mt-2 text-sm text-gray-500 dark:text-gray-400"
        >{`Image should be at least ${width}x${height} px, maximum ${
            maxSizeInBytes / 1024
        }KB, ${imageExtensions
            .join("/")
            .toUpperCase()}, maintaining the same ratio`}</p>
    );
}

function inputValidation(value, props, validationType) {
    if (props?.required && (value == null || value === "")) {
        return rules.default.required.message;
    }

    if (!props?.required && (value == null || value === "")) {
        return null;
    }

    const inputValidationType = validationType || props?.type;

    if (inputValidationType) {
        let message = rules.default.message;
        const rule = rules[inputValidationType];
        if (rule) {
            for (const [validationName, validationRule] of Object.entries(
                rule
            )) {
                const validationValue =
                    props?.[validationName] ||
                    validationRule?.value ||
                    validationRule;
                const validate = rules.validationTypes[validationName];
                if (validate(validationValue, value)) {
                    message =
                        validationRule?.message ||
                        rules.default[validationName].message;
                    return message.replace("{value}", validationValue);
                }
            }
        }
    }

    return null;
}

const validationFailed = (formState) => {
    for (const key in formState) {
        if (formState[key]?.hasError) {
            return key === "user_id" ? false : true;
        }
    }
    return false;
};

const lightenHexColor = (hex, percent) => {
    // Remove the '#' if it's there
    hex = hex.replace(/^#/, "");

    // Parse the r, g, b values
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);

    // Increase the RGB values
    r = Math.min(255, Math.round(r + r * percent));
    g = Math.min(255, Math.round(g + g * percent));
    b = Math.min(255, Math.round(b + b * percent));

    // Convert back to hex
    const toHex = (c) => {
        const hex = c.toString(16).padStart(2, "0");
        return hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const isCompanyRetailer = () =>
    getLocalUserInfo().role_type === "COMPANY_RETAILER";

function isValidUsername(username) {
    const regex = /^[a-zA-Z0-9_ !"#$%&'()*+,\-./:;<=>?@[\\\]^`{|}~]+$/;
    return regex.test(username);
}

export {
    lightenHexColor,
    inputValidation,
    capitalizeArrayItems,
    capitalizeItem,
    formatDropdown,
    parseJsonValue,
    objectToOptions,
    selectOptions,
    findObjectByKey,
    userCompanyId,
    userCounterId,
    generateUniqueKey,
    makeReadableMessage,
    formatDateToYMD,
    apiErrorHandler,
    sortByLabel,
    findSeatIndices,
    sortByTitleEN,
    sortByTitleObjectEN,
    isTitleENUnique,
    firstLetterUppercase,
    checkImageURL,
    getFileUrl,
    replacePTags,
    formatDateToYMDHMS,
    formatDateToHMS,
    alphanumericPattern,
    isValidEmail,
    isValidBDPhoneNumber,
    isValidDomain,
    isValidSubDomain,
    sortArrayByTitle,
    isValidName,
    isBangla,
    isValidAddress,
    isPositiveNumber,
    isValidURL,
    getImageInfoMessage,
    isValidBDORINPhoneNumber,
    validationFailed,
    formatDateToDMY,
    formatDateToDMYHMA,
    formatDateToDMYHMALocal,
    convertAndFormatDateToDMYHMA,
    isCompanyRetailer,
    getCompanyLogo,
    isValidUsername,
};
